import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api.ts';
import './Categories.css';

interface Category {
  id: number;
  name: string;
  description: string;
  type: 'income' | 'expense';
  icon: string;
  icon_color: string;
  is_default: boolean;
  is_itemizable: boolean;
  created_at: string;
  updated_at: string;
}

const Categories: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'expense' as 'income' | 'expense',
    icon: '📋',
    icon_color: '#667eea',
    is_default: false,
    is_itemizable: false,
  });
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const formRef = useRef<HTMLDivElement>(null);

  const formatDate = (dateString: string, includeTime: boolean = false) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Data inválida';
      }
      
      if (includeTime) {
        return date.toLocaleString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      
      return date.toLocaleDateString('pt-BR');
    } catch (error) {
      return 'Data inválida';
    }
  };


  // Lista de ícones financeiros disponíveis
  const getFinancialIcons = () => {
    return [
      { icon: '📋', name: 'Outros' },
      { icon: '💰', name: 'Dinheiro' },
      { icon: '💳', name: 'Cartão' },
      { icon: '🏦', name: 'Banco' },
      { icon: '📊', name: 'Gráfico' },
      { icon: '💸', name: 'Despesa' },
      { icon: '📈', name: 'Crescimento' },
      { icon: '🏠', name: 'Casa' },
      { icon: '🚗', name: 'Carro' },
      { icon: '🍔', name: 'Comida' },
      { icon: '⛽', name: 'Combustível' },
      { icon: '🛒', name: 'Compras' },
      { icon: '🎬', name: 'Entretenimento' },
      { icon: '🏥', name: 'Saúde' },
      { icon: '📚', name: 'Educação' },
      { icon: '✈️', name: 'Viagem' },
      { icon: '🎁', name: 'Presente' },
      { icon: '💼', name: 'Trabalho' },
      { icon: '🏋️', name: 'Esporte' },
      { icon: '🎵', name: 'Música' },
      { icon: '📱', name: 'Tecnologia' }
    ];
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // Scroll automático quando o formulário for aberto
  useEffect(() => {
    if (showForm && formRef.current) {
      // Pequeno delay para garantir que o DOM foi atualizado
      setTimeout(() => {
        formRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
        // Fallback: scroll para o topo da página se o ref não funcionar
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  }, [showForm]);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/api/v1/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Erro ao buscar categorias:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingCategory) {
        await api.put(`/api/v1/categories/${editingCategory.id}`, formData);
      } else {
        await api.post('/api/v1/categories', formData);
      }
      setFormData({ name: '', description: '', type: 'expense', icon: '📋', icon_color: '#667eea', is_default: false, is_itemizable: false });
      setShowForm(false);
      setEditingCategory(null);
      fetchCategories();
    } catch (error) {
      console.error('Erro ao salvar categoria:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (category: Category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description,
      type: category.type,
      icon: category.icon,
      icon_color: category.icon_color || '#667eea',
      is_default: category.is_default || false,
      is_itemizable: category.is_itemizable || false,
    });
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingCategory(null);
    setFormData({ name: '', description: '', type: 'expense', icon: '📋', icon_color: '#667eea', is_default: false, is_itemizable: false });
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Tem certeza que deseja excluir esta categoria?')) {
      setDeletingId(id);
      try {
        await api.delete(`/api/v1/categories/${id}`);
        fetchCategories();
      } catch (error) {
        console.error('Erro ao excluir categoria:', error);
      } finally {
        setDeletingId(null);
      }
    }
  };

  if (loading) {
    return (
      <div className="categories">
        <div className="page-header">
          <h1>📂 Categorias</h1>
          <p>Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="categories">
      <div className="page-header">
        <h1>📂 Categorias</h1>
        <p>Organize suas receitas e despesas por categoria</p>
        <button 
          className="primary-button"
          onClick={() => {
            setEditingCategory(null);
            setFormData({ name: '', description: '', type: 'expense', icon: '📋', icon_color: '#667eea', is_default: false, is_itemizable: false });
            setShowForm(!showForm);
          }}
        >
          {showForm ? 'Cancelar' : 'Nova Categoria'}
        </button>
      </div>

      {showForm && (
        <div className="form-container" ref={formRef}>
          <form onSubmit={handleSubmit} className="category-form">
            <h3>{editingCategory ? 'Editar Categoria' : 'Nova Categoria'}</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">Nome da Categoria</label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Ex: Alimentação, Transporte..."
                />
              </div>
              <div className="form-group">
                <label htmlFor="type">Tipo</label>
                <select
                  id="type"
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as 'income' | 'expense' })}
                >
                  <option value="expense">Despesa</option>
                  <option value="income">Receita</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="description">Descrição</label>
              <input
                type="text"
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descrição opcional"
              />
            </div>
            <div className="form-group">
              <label htmlFor="icon">Ícone da Categoria</label>
              <select
                id="icon"
                value={formData.icon}
                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
              >
                {getFinancialIcons().map((iconOption) => (
                  <option key={iconOption.icon} value={iconOption.icon}>
                    {iconOption.icon} {iconOption.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Toggle para definir como padrão */}
            <div className="form-group form-group-toggle">
              <div className="toggle-container">
                <div className="toggle-label-with-tooltip">
                  <label htmlFor="is_default" className="toggle-label">
                    Definir como padrão
                  </label>
                  <span className="tooltip">
                    <button
                      type="button"
                      className="tooltip-trigger"
                      aria-label="Ajuda: Definir como padrão"
                      aria-describedby="is_default_help"
                    >
                      ?
                    </button>
                    <span id="is_default_help" role="tooltip" className="tooltip-content">
                      Esta categoria será pré-selecionada ao criar novas transações do tipo {formData.type === 'income' ? 'receita' : 'despesa'}
                    </span>
                  </span>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    id="is_default"
                    checked={!!formData.is_default}
                    onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>

            {/* Toggle para categoria detalhável */}
            <div className="form-group form-group-toggle">
              <div className="toggle-container">
                <div className="toggle-label-with-tooltip">
                  <label htmlFor="is_itemizable" className="toggle-label">
                    Habilitada para Itemização
                  </label>
                  <span className="tooltip">
                    <button
                      type="button"
                      className="tooltip-trigger"
                      aria-label="Ajuda: Habilitada para Itemização"
                      aria-describedby="is_itemizable_help"
                    >
                      ?
                    </button>
                    <span id="is_itemizable_help" role="tooltip" className="tooltip-content">
                      Permite adicionar itens individuais (ex: linhas de fatura de cartão) às transações dessa categoria. O botão "📋 Itens" aparecerá em cada transação.
                    </span>
                  </span>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    id="is_itemizable"
                    checked={!!formData.is_itemizable}
                    onChange={(e) => setFormData({ ...formData, is_itemizable: e.target.checked })}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              {formData.is_itemizable && (
                <div className="itemization-rules-hint">
                  <span>Para categorizar itens automaticamente ao importar faturas, configure as </span>
                  <Link to="/itemization/rules" className="itemization-rules-link">
                    Regras de Itemização →
                  </Link>
                </div>
              )}
            </div>
            
            {/* Informações de criação e atualização - apenas no modo de edição */}
            {editingCategory && (
              <div className="category-metadata">
                <div className="metadata-item">
                  <span className="metadata-label">Criado em:</span>
                  <span className="metadata-value">{formatDate(editingCategory.created_at, true)}</span>
                </div>
                {editingCategory.updated_at && editingCategory.updated_at !== editingCategory.created_at && (
                  <div className="metadata-item">
                    <span className="metadata-label">Última atualização:</span>
                    <span className="metadata-value">{formatDate(editingCategory.updated_at, true)}</span>
                  </div>
                )}
              </div>
            )}
            
            <div className="form-actions">
              <button type="submit" className="primary-button" disabled={saving}>
                {saving ? 'Salvando...' : (editingCategory ? 'Salvar Alterações' : 'Criar Categoria')}
              </button>
              <button 
                type="button" 
                className="secondary-button"
                onClick={handleCancel}
                disabled={saving}
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="categories-content">
        {categories.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📂</div>
            <h3>Nenhuma categoria cadastrada</h3>
            <p>Crie categorias para organizar melhor suas transações</p>
            <button 
              className="primary-button"
              onClick={() => {
                setEditingCategory(null);
            setFormData({ name: '', description: '', type: 'expense', icon: '📋', icon_color: '#667eea', is_default: false, is_itemizable: false });
            setShowForm(true);
              }}
            >
              Adicionar Categoria
            </button>
          </div>
        ) : (
          <div className="categories-grid">
            {categories.map((category) => (
              <div key={category.id} className="category-card">
                <div className="category-header">
                  <div className="category-info">
                    <div 
                      className="category-icon" 
                      style={{ color: category.icon_color }}
                    >
                      {category.icon}
                    </div>
                    <h3>{category.name}</h3>
                  </div>
                  <div className="category-actions">
                    <button 
                      className="edit-button"
                      onClick={() => handleEdit(category)}
                      title="Editar categoria"
                    >
                      <img src="/edit.svg" alt="Editar" className="icon" />
                    </button>
                    <button 
                      className="delete-button"
                      onClick={() => handleDelete(category.id)}
                      title="Excluir categoria"
                      disabled={deletingId === category.id}
                    >
                      {deletingId === category.id ? '...' : <img src="/delete.svg" alt="Excluir" className="icon" />}
                    </button>
                  </div>
                </div>
                {category.description && (
                  <p className="category-description">{category.description}</p>
                )}
                <div className="category-type">
                  <span className={`type-badge ${category.type}`}>
                    {category.type === 'income' ? '💰 Receita' : '💸 Despesa'}
                  </span>
                  {category.is_itemizable && (
                    <span className="type-badge" style={{ background: '#ede9fe', color: '#5b21b6', marginLeft: '0.4rem' }}>
                      📋 Detalhável
                    </span>
                  )}
                </div>
                <div className="category-meta">
                  <small>Criada em: {formatDate(category.created_at)}</small>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Categories;